import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Menu,
  Pagination,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import {
  Plus,
  Pencil,
  Trash,
  DotsThreeVertical,
  Receipt,
  CheckCircle,
  Eye,
  Palette,
  Archive,
  TrayArrowDown,
  Warning
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import useThemeStyles from '../../../theme/useThemeStyles';
import StatusBadge from './StatusBadge';
import PaymentLinkModal from './PaymentLinkModal';

function InvoiceList() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { 
    selectedBusiness, 
    loading: businessLoading, 
    hasNoBusiness,
    error: businessError 
  } = useBusiness();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    weddingId: '',
    search: '',
    archived: false // Default to showing non-archived invoices
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [_stripeConnectStatus, setStripeConnectStatus] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      
      const params = {
        page,
        limit: 20,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.weddingId && { weddingId: filters.weddingId }),
        archived: filters.archived ? 'true' : 'false'
      };
      const response = await api.get(`/api/invoices/business/${businessId}`, { params });
      setInvoices(response.data.invoices || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.business?._id, filters, page]);

  const checkStripeConnectStatus = useCallback(async () => {
    try {
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) return;
      
      const response = await api.get(`/api/stripe-connect/status/${businessId}`);
      setStripeConnectStatus(response.data);
    } catch (error) {
      // Ignore errors - just means status check failed
      setStripeConnectStatus(null);
    }
  }, [selectedBusiness?.business?._id]);

  useEffect(() => {
    if (businessLoading) {
      return; // Wait for business to load
    }
    
    const businessId = selectedBusiness?.business?._id;
    if (!businessId) {
      setLoading(false);
      return;
    }
    
    fetchInvoices();
    checkStripeConnectStatus();
  }, [selectedBusiness, businessLoading, filters, page, fetchInvoices, checkStripeConnectStatus]);

  const handleMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;
    try {
      await api.delete(`/api/invoices/${selectedInvoice._id}`);
      fetchInvoices();
      handleMenuClose();
    } catch (error) {
      setError('Failed to delete invoice');
    }
  };

  const handleSend = async () => {
    if (!selectedInvoice) return;
    try {
      await api.post(`/api/invoices/${selectedInvoice._id}/send`);
      fetchInvoices();
      handleMenuClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send invoice');
    }
  };

  const _handleGeneratePaymentLink = async () => {
    if (!selectedInvoice) return;
    try {
      const response = await api.post(`/api/invoices/${selectedInvoice._id}/generate-payment-link`);
      setSelectedInvoice({ ...selectedInvoice, ...response.data.invoice });
      setPaymentLinkModalOpen(true);
      handleMenuClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to generate payment link');
      handleMenuClose();
    }
  };

  const handleTogglePaid = async () => {
    if (!selectedInvoice) return;
    try {
      await api.post(`/api/invoices/${selectedInvoice._id}/toggle-paid`);
      fetchInvoices();
      handleMenuClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update payment status');
      handleMenuClose();
    }
  };

  const handleArchive = async (invoiceId) => {
    try {
      await api.post(`/api/invoices/${invoiceId}/archive`);
      fetchInvoices();
    } catch (error) {
      setError('Failed to archive invoice');
    }
  };

  const handleUnarchive = async (invoiceId) => {
    try {
      await api.post(`/api/invoices/${invoiceId}/unarchive`);
      fetchInvoices();
    } catch (error) {
      setError('Failed to unarchive invoice');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const getClientDisplay = (inv) => {
    if (inv.manualRecipient?.name) return inv.manualRecipient.name;
    if (inv.wedding?.name) return inv.wedding.name;
    if (inv.wedding?.partnerDetails?.partnerOne?.name && inv.wedding?.partnerDetails?.partnerTwo?.name) {
      return `${inv.wedding.partnerDetails.partnerOne.name} & ${inv.wedding.partnerDetails.partnerTwo.name}`;
    }
    return inv.wedding?.partnerDetails?.partnerOne?.name || 'N/A';
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const clientDisplay = getClientDisplay(invoice);
      return (
        invoice.invoiceNumber.toLowerCase().includes(search) ||
        clientDisplay.toLowerCase().includes(search) ||
        invoice.manualRecipient?.email?.toLowerCase().includes(search) ||
        invoice.description?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const renderLinkMark = (surface) => (
    <Box
      aria-hidden
      sx={(theme) => ({
        position: 'absolute',
        left: surface === 'table' ? theme.spacing(2) : theme.spacing(3),
        top: 0,
        width: theme.spacing(2),
        height: surface === 'table'
          ? `calc(${theme.spacing(1.5)} + 10px)`
          : `calc(${theme.spacing(3)} + 16px)`,
        boxSizing: 'border-box',
        borderLeft: '1.5px solid',
        borderBottom: '1.5px solid',
        borderColor: 'text.secondary',
        pointerEvents: 'none'
      })}
    />
  );

  const sourceIdOf = (invoice) => {
    const source = invoice.sourceInvoice;
    if (!source) return null;
    if (typeof source === 'string') return source;
    return source._id ? String(source._id) : null;
  };

  const groupedInvoices = (() => {
    const onPage = new Set(filteredInvoices.map((invoice) => String(invoice._id)));
    const extrasByParent = new Map();
    filteredInvoices.forEach((invoice) => {
      const parentId = sourceIdOf(invoice);
      if (!parentId) return;
      if (!extrasByParent.has(parentId)) extrasByParent.set(parentId, []);
      extrasByParent.get(parentId).push(invoice);
    });

    const placed = new Set();
    const rows = [];
    filteredInvoices.forEach((invoice) => {
      const id = String(invoice._id);
      if (placed.has(id)) return;
      const parentId = sourceIdOf(invoice);
      if (parentId && onPage.has(parentId)) return;

      rows.push({ invoice, nested: Boolean(parentId) });
      placed.add(id);

      const children = extrasByParent.get(parentId && !onPage.has(parentId) ? parentId : id) || [];
      children.forEach((child) => {
        const childId = String(child._id);
        if (placed.has(childId)) return;
        rows.push({ invoice: child, nested: true });
        placed.add(childId);
      });
    });
    return rows;
  })();

  if (businessLoading || (loading && invoices.length === 0 && !error)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (hasNoBusiness || !selectedBusiness) {
    return (
      <Box sx={styles.container.page}>
        <Alert severity="info" sx={{ mb: 3 }}>
          No business found. Please register a business first.
        </Alert>
      </Box>
    );
  }

  if (businessError) {
    return (
      <Box sx={styles.container.page}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {businessError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          sx={{
            ...styles.text.sectionTitle,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
          }}
        >
          {filters.archived ? 'Invoice Archive' : 'Invoices'}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant={filters.archived ? 'outlined' : 'contained'}
            startIcon={filters.archived ? <TrayArrowDown size={20} /> : <Archive size={20} />}
            onClick={() => setFilters({ ...filters, archived: !filters.archived, page: 1 })}
            size="medium"
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            {filters.archived ? 'View Active' : 'View Archive'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={() => navigate('/supplier/invoices/new')}
            size="medium"
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            Create Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<Palette size={20} />}
            onClick={() => navigate('/supplier/invoices/settings')}
            size="medium"
            sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            Design Invoice
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stripe Connect UI temporarily hidden - payments feature coming soon */}
      {/* {stripeConnectStatus && !stripeConnectStatus.connected && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Connect your Stripe account to enable payment links for invoices.{' '}
            <Button size="small" onClick={() => navigate('/supplier/settings/stripe-connect')}>
              Connect Stripe
            </Button>
          </Typography>
        </Alert>
      )}

      {stripeConnectStatus && stripeConnectStatus.connected && !stripeConnectStatus.chargesEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Stripe account setup is incomplete. Complete onboarding to enable payment links.
          </Typography>
        </Alert>
      )} */}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container spacing={{ xs: 2, sm: 2 }}>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Invoice number, client, description..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {filteredInvoices.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Receipt size={64} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {filters.archived ? 'No archived invoices found' : 'No invoices found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {filters.archived ? 'Archived invoices will appear here' : 'Create your first invoice to get started'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<Plus size={20} />}
                onClick={() => navigate('/supplier/invoices/new')}
              >
                Create Invoice
              </Button>
              <Button
                variant="outlined"
                startIcon={<Palette size={20} />}
                onClick={() => navigate('/supplier/invoices/settings')}
              >
                Design Invoice
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <TableContainer 
            component={Paper}
            sx={{ 
              display: { xs: 'none', md: 'block' },
              overflowX: 'auto',
              maxWidth: '100%',
              width: '100%'
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedInvoices.map(({ invoice, nested }) => (
                  <TableRow key={invoice._id} hover>
                    <TableCell sx={nested ? { position: 'relative', pl: 5.5 } : undefined}>
                      {nested && renderLinkMark('table')}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {invoice.invoiceNumber}
                      </Typography>
                      {invoice.sourceInvoice?.invoiceNumber && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Extras for {invoice.sourceInvoice.invoiceNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {getClientDisplay(invoice)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(invoice.amount, invoice.currency)}
                      {invoice.status === 'partially_paid' && invoice.paymentStatus?.paidAmount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Paid {formatCurrency(invoice.paymentStatus.paidAmount, invoice.currency)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell>
                      {invoice.dueDate ? format(new Date(invoice.dueDate), 'dd MMM yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      {invoice.paymentMethod === 'payment_link' && (
                        <Chip label="Payment Link" size="small" color="success" />
                      )}
                      {invoice.paymentMethod === 'manual' && (
                        <Chip label="Manual" size="small" />
                      )}
                      {!invoice.paymentMethod && '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, invoice)}
                      >
                        <DotsThreeVertical size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Grid container spacing={2}>
              {groupedInvoices.map(({ invoice, nested }) => (
                <Grid item xs={12} key={invoice._id}>
                  <Card>
                    <CardContent sx={nested ? { position: 'relative', pl: 6 } : undefined}>
                      {nested && renderLinkMark('card')}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {invoice.invoiceNumber}
                          </Typography>
                          {invoice.sourceInvoice?.invoiceNumber && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Extras for {invoice.sourceInvoice.invoiceNumber}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {getClientDisplay(invoice)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, invoice)}
                        >
                          <DotsThreeVertical size={20} />
                        </IconButton>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Amount
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatCurrency(invoice.amount, invoice.currency)}
                          </Typography>
                          {invoice.status === 'partially_paid' && invoice.paymentStatus?.paidAmount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Paid {formatCurrency(invoice.paymentStatus.paidAmount, invoice.currency)}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <StatusBadge status={invoice.status} />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Due Date
                          </Typography>
                          <Typography variant="body2">
                            {invoice.dueDate ? format(new Date(invoice.dueDate), 'dd MMM yyyy') : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Payment Method
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {invoice.paymentMethod === 'payment_link' && (
                              <Chip label="Payment Link" size="small" color="success" />
                            )}
                            {invoice.paymentMethod === 'manual' && (
                              <Chip label="Manual" size="small" />
                            )}
                            {!invoice.paymentMethod && (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/supplier/invoices/${selectedInvoice?._id}`);
          handleMenuClose();
        }}>
          <Eye size={18} style={{ marginRight: 8 }} />
          View
        </MenuItem>
        {selectedInvoice?.status === 'draft' && (
          <>
            <MenuItem onClick={() => {
              navigate(`/supplier/invoices/${selectedInvoice?._id}/edit`);
              handleMenuClose();
            }}>
              <Pencil size={18} style={{ marginRight: 8 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleSend}>
              Send Invoice
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <Trash size={18} style={{ marginRight: 8 }} />
              Delete
            </MenuItem>
          </>
        )}
        {['sent', 'partially_paid', 'overdue'].includes(selectedInvoice?.status) && !selectedInvoice.paymentStatus.isPaid && (
          <>
            {/* Payment link generation temporarily hidden - payments feature coming soon */}
            {/* {stripeConnectStatus?.chargesEnabled && (
              <MenuItem onClick={handleGeneratePaymentLink}>
                <Copy size={18} style={{ marginRight: 8 }} />
                Generate Payment Link
              </MenuItem>
            )} */}
            <MenuItem onClick={() => {
              navigate(`/supplier/invoices/${selectedInvoice?._id}`);
              handleMenuClose();
            }}>
              <CheckCircle size={18} style={{ marginRight: 8 }} />
              Record Payment
            </MenuItem>
            {!selectedInvoice.paymentStatus.stripeConfirmed && (
              <MenuItem onClick={handleTogglePaid}>
                <CheckCircle size={18} style={{ marginRight: 8 }} />
                Mark as Paid
              </MenuItem>
            )}
          </>
        )}
        {selectedInvoice?.paymentStatus.isPaid && selectedInvoice.paymentStatus.manuallyMarkedPaid && !selectedInvoice.paymentStatus.stripeConfirmed && (
          <MenuItem onClick={handleTogglePaid}>
            <Warning size={18} style={{ marginRight: 8 }} />
            Unmark as Paid
          </MenuItem>
        )}
        <Divider />
        {selectedInvoice?.archived ? (
          <MenuItem onClick={() => {
            handleUnarchive(selectedInvoice._id);
            handleMenuClose();
          }}>
            <TrayArrowDown size={18} style={{ marginRight: 8 }} />
            Unarchive
          </MenuItem>
        ) : (
          <MenuItem onClick={() => {
            handleArchive(selectedInvoice._id);
            handleMenuClose();
          }}>
            <Archive size={18} style={{ marginRight: 8 }} />
            Archive
          </MenuItem>
        )}
      </Menu>

      {selectedInvoice && (
        <PaymentLinkModal
          open={paymentLinkModalOpen}
          onClose={() => setPaymentLinkModalOpen(false)}
          paymentLink={selectedInvoice.paymentLink?.url}
          expiresAt={selectedInvoice.paymentLink?.expiresAt}
          invoiceNumber={selectedInvoice.invoiceNumber}
        />
      )}
    </Box>
  );
}

export default InvoiceList;
